import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, InputBase, IconButton, Button, useTheme, useMediaQuery
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import dayjs from 'dayjs';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import { toast } from 'react-toastify';

const IMAGE_WIDTH = 320;
const IMAGE_HEIGHT = 200;

// Shared style for both Typography and InputBase
const sharedTextStyle = {
  fontSize: 32,
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: 0,
  border: 'none',
  background: 'none',
  outline: 'none',
  width: '100%',
  padding: 0,
  margin: 0,
  color: 'inherit',
};

const EventDetailsCard = ({
  eventDetails = {},
  isEditing = false,
  onEdit,
  onCancel,
  onSave,
  showCreateDraftButton = false,
  onCreateDraft,
  imageUrl,
  sx = {},
  onImageUpload
}) => {
  // Initialize state with proper date handling
  const [local, setLocal] = useState({
    eventName: eventDetails.eventName || '',
    description: eventDetails.description || '',
    eventVision: eventDetails.eventVision || '',
    eventTypes: Array.isArray(eventDetails.eventTypes) ? eventDetails.eventTypes : [],
    startDate: eventDetails.startDate || null,
    endDate: eventDetails.endDate || null,
    location: eventDetails.location || '',
    guestCount: typeof eventDetails.guestCount === 'number' ? eventDetails.guestCount : 0,
  });

  const [errors, setErrors] = useState({});
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Update local state when eventDetails changes
  useEffect(() => {
    if (eventDetails) {
      setLocal(prev => ({
        ...prev,
        eventName: eventDetails.eventName || prev.eventName,
        description: eventDetails.description || prev.description,
        eventVision: eventDetails.eventVision || prev.eventVision,
        eventTypes: Array.isArray(eventDetails.eventTypes) ? eventDetails.eventTypes : prev.eventTypes,
        startDate: eventDetails.startDate || prev.startDate,
        endDate: eventDetails.endDate || prev.endDate,
        location: eventDetails.location || prev.location,
        guestCount: typeof eventDetails.guestCount === 'number' ? eventDetails.guestCount : prev.guestCount,
      }));
    }
  }, [eventDetails]);

  const validateField = (field, value) => {
    switch (field) {
      case 'eventName':
        return !value?.trim() ? 'Event name is required' : '';
      case 'startDate':
        return !value ? 'Start date is required' : '';
      case 'endDate':
        return !value ? 'End date is required' : '';
      case 'guestCount':
        return !value || value < 0 ? 'Guest count must be a positive number' : '';
      default:
        return '';
    }
  };

  const handleChange = (field, value) => {
    let processedValue = value;
    
    // Handle date fields - ensure ISO format
    if (field === 'startDate' || field === 'endDate') {
      processedValue = value ? dayjs(value).toISOString() : null;
    }

    // Handle guest count
    if (field === 'guestCount') {
      processedValue = value.replace(/^0+(?=\d)/, ''); // Remove leading zeros
      processedValue = processedValue === '' ? 0 : parseInt(processedValue, 10);
      if (isNaN(processedValue)) {
        processedValue = 0;
      }
    }

    // Handle event types
    if (field === 'eventTypes') {
      processedValue = Array.isArray(value) ? value : 
        (typeof value === 'string' ? value.split(',').map(t => t.trim()).filter(Boolean) : []);
    }

    setLocal(prev => ({ ...prev, [field]: processedValue }));
    
    // Validate the field
    const error = validateField(field, processedValue);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(local).forEach(field => {
      const error = validateField(field, local[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Save data with ISO format dates
    const saveData = {
      eventName: local.eventName?.trim() || '',
      description: local.description?.trim() || '',
      eventVision: local.eventVision?.trim() || '',
      eventTypes: Array.isArray(local.eventTypes) ? local.eventTypes : [],
      startDate: local.startDate,
      endDate: local.endDate,
      location: local.location?.trim() || '',
      guestCount: typeof local.guestCount === 'number' ? local.guestCount : 0,
    };

    console.log('Saving data from EventDetailsCard:', saveData);
    onSave(saveData);
  };

  const handleImageUpload = async (event) => {
    console.log("handleImageUpload", event);
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image file size must be less than 5MB.');
        return;
      }

      setIsUploading(true);
      
      try {
        if (eventDetails.eventId) {
          // For existing events, upload to backend
          const formData = new FormData();
          formData.append('image_file', file);

          const response = await axios.put(
            `${getBackendUserUrl()}/event/update_event_cover_image/${eventDetails.eventId}`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data && response.data.imageUrl) {
            toast.success('Cover image updated successfully!');
            // You might want to trigger a refresh of the event data here
            // or pass the new image URL up to the parent component
            window.location.reload(); // Simple refresh for now
          }
        } else {
          console.log("ELSE");
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        if (error.response && error.response.data && error.response.data.detail) {
          toast.error(error.response.data.detail);
        } else {
          toast.error('Error uploading image. Please try again later.');
        }
      } finally {
        setIsUploading(false);
      }
    }
  };

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      display="flex"
      flexDirection={isSmDown ? 'column' : 'row'}
      alignItems={isSmDown ? 'stretch' : 'flex-start'}
      gap={4}
      sx={{
        borderRadius: 3,
        p: 0,
        minHeight: IMAGE_HEIGHT,
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        bgcolor: 'background.default',
        ...sx,
      }}
    >
      {/* Image and button */}
      <Box
        sx={{
          position: 'relative',
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#f5f5f5',
          flexShrink: 0,
          cursor: 'pointer',
          ...(isSmDown && { width: '100%', minWidth: 0, mb: 2 })
        }}
        onMouseEnter={() => setIsImageHovered(true)}
        onMouseLeave={() => setIsImageHovered(false)}
      >
        {/* Hidden file input - always available */}
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="event-cover-upload"
          type="file"
          onChange={handleImageUpload}
        />

        {imageUrl && imageUrl !== 'default-image-url.jpg' ? (
          <>
            <img
              src={imageUrl}
              alt="Event"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                minHeight: IMAGE_HEIGHT,
                minWidth: IMAGE_WIDTH,
                filter: 'blur(0.5px)'
              }}
            />
            {/* Hover overlay for image upload */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isImageHovered ? 1 : 0,
                transition: 'opacity 0.3s ease',
                zIndex: 1,
              }}
            >
              <label htmlFor="event-cover-upload" style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
                <Button
                  component="span"
                  startIcon={isUploading ? null : <PhotoCameraIcon />}
                  variant="contained"
                  disabled={isUploading}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: 'text.primary',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(255, 255, 255, 0.7)',
                      color: 'text.secondary',
                    },
                    boxShadow: 3,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  {isUploading ? 'Uploading...' : 'Change Image'}
                </Button>
              </label>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.200'
            }}
          >
            <label htmlFor="event-cover-upload" style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
              <Button
                component="span"
                startIcon={isUploading ? null : <CloudUploadIcon />}
                variant="contained"
                disabled={isUploading}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 1)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    color: 'text.secondary',
                  },
                  boxShadow: 2,
                  fontWeight: 600
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload Cover Image'}
              </Button>
            </label>
          </Box>
        )}
        
        {/* Overlay for button readability */}
        {showCreateDraftButton && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(0,0,0,0.45)',
                borderRadius: 2,
                px: 2,
                py: 1,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  borderRadius: 8,
                  boxShadow: 2,
                  whiteSpace: 'nowrap',
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
                onClick={onCreateDraft}
              >
                CREATE EVENT DRAFT
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      {/* Details */}
      <Box
        flex={1}
        sx={{
          py: 2,
          pr: 2,
          minHeight: IMAGE_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {/* Responsive header row for title and buttons */}
        <Box
          display="flex"
          flexDirection={isSmDown ? 'column' : 'row'}
          alignItems={isSmDown ? 'flex-start' : 'center'}
          justifyContent="space-between"
          mb={1}
          gap={isSmDown ? 1 : 0}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: isSmDown ? '100%' : 'calc(100% - 56px)',
              mr: isSmDown ? 0 : 2,
              display: 'flex',
              alignItems: isSmDown ? 'flex-start' : 'center',
            }}
          >
            {isEditing ? (
              <InputBase
                value={local.eventName}
                onChange={e => handleChange('eventName', e.target.value)}
                sx={{
                  ...sharedTextStyle,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  maxHeight: 80,
                  overflowY: 'auto',
                  display: 'block',
                  width: '100%',
                  border: errors.eventName ? '1px solid red' : 'none',
                }}
                inputProps={{ 'aria-label': 'Event Name', style: { ...sharedTextStyle, whiteSpace: 'pre-line', wordBreak: 'break-word' } }}
                placeholder="Event Name"
                multiline
              />
            ) : (
              <Typography
                component="div"
                sx={{
                  ...sharedTextStyle,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: 80,
                  width: '100%',
                }}
                noWrap={false}
              >
                {local.eventName || 'Untitled Event'}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: isSmDown ? '100%' : 56,
              minWidth: 56,
              display: 'flex',
              justifyContent: isSmDown ? 'flex-end' : 'flex-end',
              alignItems: 'center',
              mt: isSmDown ? 1 : 0,
            }}
          >
            {isEditing ? (
              <>
                <IconButton onClick={handleSave} color="primary" size="large"><SaveIcon /></IconButton>
                <IconButton onClick={onCancel} size="large"><CancelIcon /></IconButton>
              </>
            ) : (
              <IconButton onClick={onEdit} size="large"><EditIcon /></IconButton>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon color="primary" />
            {isEditing ? (
              <>
                <InputBase
                  type="datetime-local"
                  value={local.startDate ? dayjs(local.startDate).format('YYYY-MM-DDTHH:mm') : ''}
                  onChange={e => handleChange('startDate', e.target.value)}
                  sx={{ 
                    minWidth: 180, 
                    fontSize: 16, 
                    mr: 1,
                    border: errors.startDate ? '1px solid red' : 'none',
                  }}
                  inputProps={{ 'aria-label': 'Start Date', style: { fontSize: 16 } }}
                />
                <Typography>-</Typography>
                <InputBase
                  type="datetime-local"
                  value={local.endDate ? dayjs(local.endDate).format('YYYY-MM-DDTHH:mm') : ''}
                  onChange={e => handleChange('endDate', e.target.value)}
                  sx={{ 
                    minWidth: 180, 
                    fontSize: 16,
                    border: errors.endDate ? '1px solid red' : 'none',
                  }}
                  inputProps={{ 'aria-label': 'End Date', style: { fontSize: 16 } }}
                />
              </>
            ) : (
              <Typography>
                {local.startDate && local.endDate
                  ? `${dayjs(local.startDate).format('M/D/YYYY h:mmA')} - ${dayjs(local.endDate).format('M/D/YYYY h:mmA')}`
                  : 'No date set'}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <PlaceIcon color="primary" />
            {isEditing ? (
              <InputBase
                value={local.location}
                onChange={e => handleChange('location', e.target.value)}
                sx={{ minWidth: 120, fontSize: 16 }}
                inputProps={{ 'aria-label': 'Location', style: { fontSize: 16 } }}
                placeholder="Location"
              />
            ) : (
              <Typography>{local.location}</Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <PeopleIcon color="primary" />
            {isEditing ? (
              <InputBase
                type="number"
                value={local.guestCount}
                onChange={e => handleChange('guestCount', e.target.value)}
                sx={{ 
                  width: 80, 
                  fontSize: 16,
                  border: errors.guestCount ? '1px solid red' : 'none',
                }}
                inputProps={{ 'aria-label': 'Guest Count', style: { fontSize: 16 } }}
                placeholder="Guests"
              />
            ) : (
              <Typography>{local.guestCount}</Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography fontWeight={500}>Event Type:</Typography>
          {isEditing ? (
            <InputBase
              value={(local.eventTypes || []).join(', ')}
              onChange={e => handleChange('eventTypes', e.target.value.split(',').map(t => t.trim()))}
              sx={{ minWidth: 200, fontSize: 16 }}
              inputProps={{ 'aria-label': 'Event Types', style: { fontSize: 16 } }}
              placeholder="Party, Charity, ..."
            />
          ) : (
            (local.eventTypes && local.eventTypes.length > 0 ? local.eventTypes : ['Party']).map(type => (
              <Chip key={type} label={type} color="primary" variant="outlined" sx={{ mr: 1 }} />
            ))
          )}
        </Box>
        {/* Description: always show section header */}
        <Box mb={1}>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>Event Vision</Typography>
          {isEditing ? (
            <InputBase
              value={local.eventVision}
              onChange={e => handleChange('eventVision', e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              placeholder="Event description"
              sx={{ fontSize: 16, width: '100%', mb: 1, maxHeight: 120, overflowY: 'auto' }}
              inputProps={{ 'aria-label': 'Event Vision', style: { fontSize: 16 } }}
            />
          ) : (
            <Typography
              color="text.secondary"
              sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', maxHeight: 120, overflowY: 'auto' }}
            >
              {local.eventVision || 'No description set.'}
            </Typography>
          )}
        </Box>
        <Box mb={1}>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>Event Description</Typography>
          {isEditing ? (
            <InputBase
              value={local.description}
              onChange={e => handleChange('description', e.target.value)}
              multiline
              minRows={2}
              fullWidth
              placeholder="Event description"
              sx={{ fontSize: 16, width: '100%' }}
              inputProps={{ 'aria-label': 'Event Description', style: { fontSize: 16 } }}
            />
          ) : (
            <Typography color="text.secondary">
              {local.description || 'No description set.'}
            </Typography>
          )}
        </Box>
        {/* Error Messages */}
        {isEditing && Object.keys(errors).length > 0 && (
          <Box mt={2}>
            {Object.entries(errors).map(([field, error]) => (
              error && (
                <Typography key={field} color="error" variant="caption" display="block">
                  {error}
                </Typography>
              )
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EventDetailsCard; 